import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import {
  Visibility,
  Favorite,
  ChatBubble,
  Share,
} from '@mui/icons-material';
import AnalyticsChart from './AnalyticsChart';
import MetricsCard from './MetricsCard';
import { Property } from '@/types';

interface PropertyAnalyticsProps {
  properties: Property[];
}

const PropertyAnalytics: React.FC<PropertyAnalyticsProps> = ({ properties }) => {
  const aggregatedStats = useMemo(() => {
    let totalViews = 0;
    let totalFavorites = 0;
    let totalInquiries = 0;
    let totalShares = 0;

    properties.forEach((p) => {
      const a = p.analytics;
      if (a) {
        totalViews += a.views || 0;
        totalFavorites += a.favorites || 0;
        totalInquiries += a.inquiries || 0;
        totalShares += a.shares || 0;
      }
    });

    return { totalViews, totalFavorites, totalInquiries, totalShares };
  }, [properties]);

  const typeDistribution = useMemo(() => {
    const distribution: Record<string, number> = {};
    properties.forEach((p) => {
      const t = p.metadata.propertyType || 'Unknown';
      distribution[t] = (distribution[t] || 0) + 1;
    });

    return Object.entries(distribution).map(([name, value]) => ({ name, value }));
  }, [properties]);

  // Mock time-series data for the last 7 days
  const timeSeriesData = useMemo(() => {
    const data = [];
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const baseViews = Math.floor(aggregatedStats.totalViews / 7) || 15;
    for (let i = 0; i < 7; i++) {
      data.push({
        name: days[i],
        views: baseViews + Math.floor(Math.random() * 20 - 10),
        inquiries: Math.floor(baseViews / 5) + Math.floor(Math.random() * 5),
      });
    }
    return data;
  }, [aggregatedStats]);

  const topProperties = useMemo(() => {
    return [...properties]
      .sort((a, b) => ((b.analytics?.views || 0) - (a.analytics?.views || 0)))
      .slice(0, 5);
  }, [properties]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        Property Performance Overview
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Total Views"
            value={aggregatedStats.totalViews}
            icon={<Visibility />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Favorites"
            value={aggregatedStats.totalFavorites}
            icon={<Favorite />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Inquiries"
            value={aggregatedStats.totalInquiries}
            icon={<ChatBubble />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricsCard
            title="Shares"
            value={aggregatedStats.totalShares}
            icon={<Share />}
            color="secondary"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <AnalyticsChart
            title="Engagement Over Time (Last 7 Days)"
            data={timeSeriesData}
            type="line"
            dataKey="views"
            height={300}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AnalyticsChart
            title="Properties by Type"
            data={typeDistribution}
            type="pie"
            dataKey="value"
            height={300}
          />
        </Grid>
      </Grid>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
            Top Performing Properties
          </Typography>
          <Stack spacing={2}>
            {topProperties.map((p, idx) => (
              <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary', width: 24 }}>
                    #{idx + 1}
                  </Typography>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{p.metadata.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.metadata.price.toLocaleString()} {p.metadata.currency} • {p.location.city}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip icon={<Visibility fontSize="small" />} label={p.analytics?.views || 0} size="small" variant="outlined" />
                  <Chip icon={<Favorite fontSize="small" />} label={p.analytics?.favorites || 0} size="small" variant="outlined" color="error" />
                </Box>
              </Box>
            ))}
            {topProperties.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No active properties available to track.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PropertyAnalytics;
